import { z } from 'zod';
import { FactProvenance, FactSource } from './fact-provenance.js';
/**
 * LearnedFact — the wire shape for a single AI-derived attribute about
 * the signed-in user, surfaced on the v7 personal profile's "What I've
 * learned" section (epic #123, Phase 2).
 *
 * Each row carries:
 *   - `id`        — stable slug ('career-background' | 'past-roles' | …)
 *                   so the correction endpoint can target one row.
 *   - `label`     — server-resolved row title (the client doesn't translate;
 *                   the i18n table is en-only today, and the row labels are
 *                   typed alongside `STRINGS['profile.learned.row.*.label']`).
 *   - `value`     — the AI-synthesized text. May be a prose paragraph (career
 *                   background) or a comma-joined enumeration (past roles).
 *   - `provenance`— the shared {@link FactProvenance} block — source +
 *                   confidence + scan history + whereUsed surfaces.
 *
 * Per-user resource (`req.uid`-scoped), not per-org. The endpoint
 * deliberately doesn't take an `orgId` because a user belongs to N orgs
 * but only one identity; the learned facts are about *them*, not the
 * tenant. Audit fan-out for the read uses `orgId: 'system'`, matching
 * the `/onboarding/enrich-*` pre-org-context routes.
 */
export const LearnedFact = z.object({
    id: z.string().min(1),
    label: z.string().min(1).max(80),
    /**
     * AI-synthesized value. Range allows up to 4000 chars so a career-
     * background prose paragraph isn't truncated. Empty values are valid
     * (the consumer renders "—" or hides the row); we don't store an
     * undefined-stub.
     */
    value: z.string().min(0).max(4000),
    provenance: FactProvenance,
});
/**
 * NarrativeLearnedFact — prose dossier with inline `[1] [2]` citation
 * refs that resolve to the `citations` table. Different shape than the
 * row-style `LearnedFact` (which is a single `value: string`): the
 * narrative card on the personal profile renders a short paragraph with
 * numeric refs and a Sources table beneath. Built on the same
 * `FactProvenance` block so the source pill + confidence meter + "Used
 * in" row stay identical to the row-style card.
 *
 * The `summary` is the prose; each entry in `citations[]` has a `ref`
 * (the 1-indexed numeric token the prose embeds as `[1]`) plus the
 * `sourceUrl` + `sourceKind` so the references table can render an icon
 * per row. Per CLAUDE.md, every AI-surfaced claim cites its source.
 */
export const NarrativeCitation = z.object({
    ref: z.number().int().positive(),
    sourceUrl: z.string().url().max(500),
    sourceKind: FactSource,
    title: z.string().max(300).optional(),
});
export const NarrativeLearnedFact = z.object({
    id: z.string().min(1),
    label: z.string().min(1).max(80),
    summary: z.string().min(0).max(4000),
    citations: z.array(NarrativeCitation).max(20).default([]),
    provenance: FactProvenance,
});
export const LearnedFactsResponse = z.object({
    facts: z.array(LearnedFact),
    /**
     * Prose dossier blocks rendered above the row-style facts. Optional
     * so existing callers stay shape-compatible — a route that doesn't
     * synthesize a narrative just omits the array.
     */
    narratives: z.array(NarrativeLearnedFact).optional(),
});
/**
 * Body of `POST /api/me/learned-facts/:factId/correct`. The user
 * submits free-text describing what's wrong + what should be there
 * instead. Phase 2 records the audit event; backend processing of the
 * correction (re-running synthesis on the affected fact, persisting an
 * override) is tracked as a follow-up under epic #123.
 */
export const LearnedFactCorrectRequest = z.object({
    correction: z.string().min(1).max(2000),
});
